import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../services/api';
import { XCircle, RefreshCw, ArrowLeft } from 'lucide-react';

const PaymentFailure = () => {
  const [searchParams] = useSearchParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  const txnid = searchParams.get('txnid');

  useEffect(() => {
    if (txnid) {
      fetchTransactionStatus();
    } else {
      setLoading(false);
    }
  }, [txnid]);

  const fetchTransactionStatus = async () => {
    try {
      const response = await paymentsAPI.getStatus(txnid);
      setTransaction(response.data.data);
    } catch (error) {
      console.error('Error fetching transaction status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 text-center text-earth-500">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600 mx-auto"></div>
        <p className="mt-4">Checking payment status...</p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-4 max-w-2xl mx-auto bg-background min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="bg-card border border-earth-200 rounded-2xl p-8 text-center"
      >
        <div className="mb-6">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-serif font-bold mb-2 text-earth-900">
            Payment Failed
          </h1>
          <p className="text-earth-600 text-lg">
            We're sorry, but your payment could not be processed. Please try again.
          </p>
        </div>

        {transaction && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-red-800 mb-4">
              Transaction Details
            </h2>
            <div className="space-y-2 text-left">
              <div className="flex justify-between">
                <span className="text-earth-600">Course:</span>
                <span className="font-medium text-earth-900">{transaction.course_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Amount:</span>
                <span className="font-medium text-earth-900">₹{transaction.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Transaction ID:</span>
                <span className="font-medium text-earth-900 text-sm">{transaction.txnid}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Status:</span>
                <span className="font-medium text-red-600 capitalize">{transaction.status}</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full py-3 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition-colors font-medium"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Try Again
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full py-3 border border-earth-300 text-earth-700 rounded-lg hover:bg-earth-50 transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Courses
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-earth-200">
          <p className="text-earth-500 text-sm mb-4">
            If you continue to experience issues, please contact our support team.
          </p>
          <div className="text-sm text-earth-600">
            <p>Common reasons for payment failure:</p>
            <ul className="mt-2 space-y-1 text-left max-w-md mx-auto">
              <li>• Insufficient funds in your account</li>
              <li>• Incorrect card details</li>
              <li>• Network connectivity issues</li>
              <li>• Bank declined the transaction</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentFailure; 